import { Alert } from '@ksyos/design-system';
import { ExaminationDetailsModal } from '@frontend/components/pages/episodeCategory/customControls/osas/osasReferral/components/ExaminationDetailsModal';
import {
    retrognathismOptions,
    tongueSizeOptions,
    tonsilSizeOptions,
} from '@frontend/components/pages/episodeCategory/customControls/osas/osasReferral/components/ImageSelect';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IRequest } from '../../../../interfaces/request';
import type { IDiagnosticReportAddEdit } from '../../../diagnosticReports/DiagnosticReportItemAddEdit';
import { ComorbiditiesChoice } from '../../../../../../common/enums/comorbiditiesChoice';
import { ConsultationPlace } from '../../../../../../common/enums/consultationPlace';
import { Retrognathism } from '../../../../../../common/enums/physicalExamination/retrognathism';
import { TongueSize } from '../../../../../../common/enums/physicalExamination/tongueSize';
import { TonsilSize } from '../../../../../../common/enums/physicalExamination/tonsilSize';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { translateKey } from '../../../../../translate';
import { isCustomControlComplete } from '../../../../../utils/isCustomControlDataComplete';
import { FormDivider, HeaderDivider, InputCheckbox, InputRadioGroup, InputTextArea } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { StackedFormGroup } from '../../../../elements/forms/StackedFormGroup';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { ImageSelection } from '../../../../fields/ImageSelection';
import { RadioGroupField, RadioGroupInput } from '../../../../fields/RadioGroup';
import { TextAreaField, TextAreaInput } from '../../../../fields/TextArea';
import { BmiAddEdit } from '../../utils/v1/BmiAddEdit';
import { anamnesisFieldsKsyosSleep, anamnesisFieldsNsi } from './anamnesisFields';

export interface IOsasc1CustomControlData {
    ademstops_remark: string | null;
    ademstops_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    anamnese_overig: string | null;
    bmiMeasuredBy: string | null;
    bmiMeasuredDate: string | null;
    comorbiditeiten: IComorbiditeitenKsyosMedicalCenter;
    comorbiditeitenChoice: ComorbiditiesChoice | null;
    concentratieverlies_remark: string | null;
    concentratieverlies_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    consultationPlace: ConsultationPlace | null;
    dentitie_goed_gesaneerd_remark: string | null;
    dentitie_goed_gesaneerd_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    ervaring_te_stikken_tijdens_slaap_remark: string | null;
    ervaring_te_stikken_tijdens_slaap_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    gebitsprothese_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    geheugenproblemen_remark: string | null;
    geheugenproblemen_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    gewicht: number | null;
    herhaald_wakker_schrikken_remark: string | null;
    herhaald_wakker_schrikken_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    humeur_kort_lontje_depressieve_gevoelens_remark: string | null;
    humeur_kort_lontje_depressieve_gevoelens_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    lengte: number | null;
    niet_verfrissende_slaap_remark: string | null;
    niet_verfrissende_slaap_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    overmatige_slaperigheid_overdag_remark: string | null;
    overmatige_slaperigheid_overdag_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    poligraphyExam: 'Ja' | 'Nee' | null;
    retrognathism: Retrognathism | null;
    snurken_remark: string | null;
    snurken_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    somnologyOrganizationType: SomnologyOrganizationType | null;
    stokkende_ademhaling_remark: string | null;
    stokkende_ademhaling_value: 'Ja' | 'Nee' | 'Onbekend' | null;
    tongueSize: TongueSize | null;
    tonsilSize: TonsilSize | null;
    vermoeidheid_overdag_remark: string | null;
    vermoeidheid_overdag_value: 'Ja' | 'Nee' | 'Onbekend' | null;
}

export interface IComorbiditeitenKsyosMedicalCenter {
    anders_checked: boolean;
    anders_text: string | null;
    burn_out_of_depressieve_klachten: boolean;
    cardiale_problematiek: boolean;
    diabetes_mellitus: boolean;
    hoge_bloeddruk: boolean;
}

interface IOsasc1V1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: any;
    diagnosticReport?: IDiagnosticReportAddEdit;
    errors: ValidationErrors;
    handleChange: (name: any, value: any) => Promise<void>;
    handleChangeCustomControl: (values: Partial<IOsasc1CustomControlData>) => Promise<void>;
    request: IRequest;
    validateOne: (name: any) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export class Osasc1V1DiagnosticReportAddEditCustomControlFields extends Component<IOsasc1V1DiagnosticReportAddEditCustomControlFieldsProps> {
    public constructor(props: IOsasc1V1DiagnosticReportAddEditCustomControlFieldsProps) {
        super(props);
    }

    public render() {
        const initializedCustomControlData = osasc1V1DiagnosticReportInitializeCustomControlData();
        const complete =
            isCustomControlComplete(initializedCustomControlData, this.props.customControlData) &&
            isCustomControlComplete(
                initializedCustomControlData.comorbiditeiten,
                this.props.customControlData.comorbiditeiten
            );

        if (!complete) {
            return null;
        }

        const comorbiditeitenChoiceNee = this.props.customControlData.comorbiditeitenChoice === ComorbiditiesChoice.nee;

        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'diagnosticReport',
                    item: this.props.customControlData,
                    errors: this.props.errors,
                    onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await this.validateOneCustomControl(key),
                }}
            >
                <HeaderDivider text={translateKey('carePlan.anamnese')} />

                {this.showAnamnesisFields()}

                {this.props.request.customControlData.somnologyOrganizationType ===
                    SomnologyOrganizationType.ksyosMedicalCenter && (
                    <>
                        <TextAreaField
                            automation="anamnese_overig"
                            error={this.props.errors.get('customControlData.anamnese_overig')}
                            fieldKey="anamnese_overig"
                            hasErrors={this.props.errors.has('customControlData.anamnese_overig')}
                            label="Anamnese overig"
                            maxLength={2000}
                            placeholder="Overige anamnese, toelichting of opmerking..."
                        />

                        <HorizontalFormGroup
                            hasErrors={
                                this.props.errors.has('customControlData.comorbiditeitenChoice') ||
                                this.props.errors.has('customControlData.comorbiditeiten')
                            }
                        >
                            <FormLabel isRequired>Is er sprake van relevante comorbiditeiten?</FormLabel>
                            <InputContainer>
                                <RadioGroupInput
                                    fieldKey="comorbiditeitenChoice"
                                    options={[ComorbiditiesChoice.ja]}
                                />
                                <InputContainer>
                                    <InputCheckbox
                                        checked={this.props.customControlData.comorbiditeiten.hoge_bloeddruk}
                                        disabled={comorbiditeitenChoiceNee}
                                        onChange={async (value) =>
                                            await this.handleChangeComorbiditeiten('hoge_bloeddruk', value)
                                        }
                                        onDoValidate={async () => {
                                            await this.validateOneCustomControl('comorbiditeiten');
                                            await this.validateOneCustomControl('comorbiditeitenChoice');
                                        }}
                                        text="Hoge bloeddruk"
                                    />
                                    <InputCheckbox
                                        checked={this.props.customControlData.comorbiditeiten.cardiale_problematiek}
                                        disabled={comorbiditeitenChoiceNee}
                                        onChange={async (value) =>
                                            await this.handleChangeComorbiditeiten('cardiale_problematiek', value)
                                        }
                                        onDoValidate={async () => {
                                            await this.validateOneCustomControl('comorbiditeiten');
                                            await this.validateOneCustomControl('comorbiditeitenChoice');
                                        }}
                                        text="Cardiale problematiek"
                                    />
                                    <InputCheckbox
                                        checked={this.props.customControlData.comorbiditeiten.diabetes_mellitus}
                                        disabled={comorbiditeitenChoiceNee}
                                        onChange={async (value) =>
                                            await this.handleChangeComorbiditeiten('diabetes_mellitus', value)
                                        }
                                        onDoValidate={async () => {
                                            await this.validateOneCustomControl('comorbiditeiten');
                                            await this.validateOneCustomControl('comorbiditeitenChoice');
                                        }}
                                        text="Diabetes mellitus"
                                    />
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.comorbiditeiten
                                                .burn_out_of_depressieve_klachten
                                        }
                                        disabled={comorbiditeitenChoiceNee}
                                        onChange={async (value) =>
                                            await this.handleChangeComorbiditeiten(
                                                'burn_out_of_depressieve_klachten',
                                                value
                                            )
                                        }
                                        onDoValidate={async () => {
                                            await this.validateOneCustomControl('comorbiditeiten');
                                            await this.validateOneCustomControl('comorbiditeitenChoice');
                                        }}
                                        text="Burn-out of depressieve klachten"
                                    />
                                    <InputTextCheckbox
                                        disabled={comorbiditeitenChoiceNee}
                                        error={this.props.errors.get('customControlData.comorbiditeiten')}
                                        labelStyle={{ width: '100%' }}
                                        onChange={async (value, andersChecked) => {
                                            await this.props.handleChangeCustomControl({
                                                comorbiditeiten: {
                                                    ...this.props.customControlData.comorbiditeiten,
                                                    anders_checked: !!andersChecked,
                                                    anders_text: value,
                                                },
                                            });
                                            await this.props.handleChangeCustomControl({
                                                comorbiditeitenChoice: ComorbiditiesChoice.ja,
                                            });
                                        }}
                                        onDoValidate={async () => {
                                            await this.validateOneCustomControl('comorbiditeiten');
                                            await this.validateOneCustomControl('comorbiditeitenChoice');
                                        }}
                                        placeholder="Anders, namelijk..."
                                        text={this.props.customControlData.comorbiditeiten.anders_text}
                                    />
                                </InputContainer>
                                <RadioGroupInput
                                    automation="comorbiditeitenChoiceNee"
                                    error={this.props.errors.get('customControlData.comorbiditeitenChoice')}
                                    fieldKey="comorbiditeitenChoice"
                                    onChange={async (value) => {
                                        await this.props.handleChangeCustomControl({
                                            comorbiditeitenChoice: value,
                                            comorbiditeiten: {
                                                hoge_bloeddruk: false,
                                                cardiale_problematiek: false,
                                                diabetes_mellitus: false,
                                                burn_out_of_depressieve_klachten: false,
                                                anders_text: null,
                                                anders_checked: false,
                                            },
                                        });
                                    }}
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('comorbiditeiten');
                                        await this.validateOneCustomControl('comorbiditeitenChoice');
                                    }}
                                    options={[ComorbiditiesChoice.nee]}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    </>
                )}

                <FormDivider />
                <HeaderDivider text={translateKey('physicalExamination.physicalExamination')} />
                {this.props.customControlData.somnologyOrganizationType ===
                    SomnologyOrganizationType.ksyosMedicalCenter && (
                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.consultationPlace')}>
                        <FormLabel isRequired>{translateKey('physicalExamination.consultationPlace')}</FormLabel>
                        <InputContainer>
                            <InputRadioGroup
                                automation="consultationPlace"
                                error={this.props.errors.get('customControlData.consultationPlace')}
                                labels={[ConsultationPlace.atConsult, ConsultationPlace.remotely]}
                                name="consultationPlace"
                                onChange={async (value) => {
                                    let images: Partial<IOsasc1CustomControlData> = {};
                                    if (value === ConsultationPlace.remotely) {
                                        images = {
                                            tonsilSize: this.props.customControlData.tonsilSize ?? TonsilSize.onbekend,
                                            tongueSize: this.props.customControlData.tongueSize ?? TongueSize.onbekend,
                                            retrognathism:
                                                this.props.customControlData.retrognathism ?? Retrognathism.onbekend,
                                        };
                                    }

                                    await this.props.handleChangeCustomControl({ ...images, consultationPlace: value });
                                }}
                                onDoValidate={async () => await this.validateOneCustomControl('consultationPlace')}
                                options={[ConsultationPlace.atConsult, ConsultationPlace.remotely]}
                                value={this.props.customControlData.consultationPlace}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}

                {this.props.customControlData.consultationPlace && <ExaminationDetailsModal />}

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.tonsilSize')}>
                    <FormLabel clarification={translateKey('common.tickTheRightImage')}>
                        {translateKey('physicalExamination.tonsilSize')}
                    </FormLabel>
                    <InputContainer>
                        <ImageSelection
                            automation="tonsilSize"
                            error={this.props.errors.get('customControlData.tonsilSize')}
                            fieldKey="tonsilSize"
                            name="tonsilSize"
                            options={tonsilSizeOptions}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.tongueSize')}>
                    <FormLabel clarification={translateKey('common.tickTheRightImage')}>
                        {translateKey('physicalExamination.tongueSize')}
                    </FormLabel>
                    <InputContainer>
                        <ImageSelection
                            automation="tongueSize"
                            error={this.props.errors.get('customControlData.tongueSize')}
                            fieldKey="tongueSize"
                            name="tongueSize"
                            options={tongueSizeOptions}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {this.props.request.customControlData.somnologyOrganizationType !==
                    SomnologyOrganizationType.ksyosMedicalCenter && (
                    <>
                        <FormDivider />
                        {this.showPolicy() && (
                            <>
                                <HeaderDivider text={translateKey('diagnosticReport.policy')} />
                                {this.examinationRequired() && (
                                    <Alert appearance="info">Slaaponderzoek geïndiceerd. Je kunt verder gaan.</Alert>
                                )}
                                {!this.examinationRequired() && this.props.diagnosticReport && (
                                    <>
                                        <Alert appearance="warning">
                                            Je anamnese toont geen noodzaak voor een slaaponderzoek aan. Neem contact op
                                            met de somnoloog (tel: 085 0710900, bereikbaar tussen 9:00-17:00) om dit te
                                            bespreken.
                                        </Alert>

                                        <RadioGroupField
                                            automation="poligraphyExam"
                                            error={this.props.errors.get('customControlData.poligraphyExam')}
                                            fieldKey="poligraphyExam"
                                            hasErrors={this.props.errors.has('customControlData.poligraphyExam')}
                                            isRequired
                                            name="poligraphyExam"
                                            options={['Ja', 'Nee']}
                                            value={this.props.customControlData.poligraphyExam}
                                        />
                                        <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                                            <FormLabel isRequired>{translateKey('common.remarks')}</FormLabel>
                                            <InputContainer>
                                                <InputTextArea
                                                    automation="remark"
                                                    error={this.props.errors.get('remark')}
                                                    onChange={async (value) =>
                                                        await this.props.handleChange('remark', value)
                                                    }
                                                    onDoValidate={async () => await this.props.validateOne('remark')}
                                                    placeholder={'Vul in na overleg met somnoloog...'}
                                                    value={this.props.diagnosticReport.remark}
                                                />
                                            </InputContainer>
                                        </HorizontalFormGroup>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                {this.props.request.customControlData.somnologyOrganizationType ===
                    SomnologyOrganizationType.ksyosMedicalCenter && (
                    <>
                        <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.retrognathism')}>
                            <FormLabel clarification={translateKey('common.tickTheRightImage')}>
                                {translateKey('physicalExamination.retrognathism')}
                            </FormLabel>
                            <InputContainer>
                                <ImageSelection
                                    automation="retrognathism"
                                    error={this.props.errors.get('customControlData.retrognathism')}
                                    fieldKey="retrognathism"
                                    name="retrognathism"
                                    options={retrognathismOptions}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>

                        <FormDivider />
                        <StackedFormGroup
                            hasErrors={
                                this.props.errors.has('customControlData.dentitie_goed_gesaneerd_value') ||
                                this.props.errors.has('customControlData.dentitie_goed_gesaneerd_remark')
                            }
                        >
                            <RadioGroupField
                                automation="dentitie_goed_gesaneerd_value"
                                error={this.props.errors.get('customControlData.dentitie_goed_gesaneerd_value')}
                                fieldKey="dentitie_goed_gesaneerd_value"
                                isRequired
                                label="Dentitie goed gesaneerd"
                                name="dentitie_goed_gesaneerd_value"
                                options={['Ja', 'Nee', 'Onbekend']}
                            />
                            <TextAreaField
                                automation="dentitie_goed_gesaneerd_remark"
                                error={this.props.errors.get('customControlData.dentitie_goed_gesaneerd_remark')}
                                fieldKey="dentitie_goed_gesaneerd_remark"
                                label=""
                                placeholder="Toelichting dentitie..."
                                value={this.props.customControlData.dentitie_goed_gesaneerd_remark}
                            />
                        </StackedFormGroup>

                        <StackedFormGroup hasErrors={this.props.errors.has('customControlData.gebitsprothese_value')}>
                            <RadioGroupField
                                automation="gebitsprothese_value"
                                error={this.props.errors.get('customControlData.gebitsprothese_value')}
                                fieldKey="gebitsprothese_value"
                                isRequired
                                label="Gebitsprothese"
                                name="gebitsprothese_value"
                                options={['Ja', 'Nee', 'Onbekend']}
                            />
                        </StackedFormGroup>

                        <BmiAddEdit
                            customControlData={this.props.customControlData}
                            errors={this.props.errors}
                            required
                        />
                        <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.bmiMeasuredDate')}>
                            <FormLabel>Laatste meting uitgevoerd op</FormLabel>
                            <InputContainer>
                                <TextAreaInput
                                    automation="bmiMeasuredDate"
                                    error={this.props.errors.get('customControlData.bmiMeasuredDate')}
                                    fieldKey="bmiMeasuredDate"
                                    placeholder="Datum / periode (hoeft niet exact)"
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                        <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.bmiMeasuredBy')}>
                            <FormLabel>Meting uitgevoerd door</FormLabel>
                            <InputContainer>
                                <TextAreaInput
                                    automation="bmiMeasuredBy"
                                    error={this.props.errors.get('customControlData.bmiMeasuredBy')}
                                    fieldKey="bmiMeasuredBy"
                                    placeholder="Bijv. Patiënt"
                                />
                            </InputContainer>
                        </HorizontalFormGroup>

                        {this.props.customControlData.gewicht &&
                            (this.props.customControlData.gewicht < 30 ||
                                this.props.customControlData.gewicht > 300) && (
                                <Alert appearance="info">Vul een gewicht tussen 30 kg en 300 kg in.</Alert>
                            )}

                        {this.props.customControlData.lengte &&
                            (this.props.customControlData.lengte < 100 ||
                                this.props.customControlData.lengte > 230) && (
                                <Alert appearance="info">Vul een lengte tussen 100 cm en 230 cm in.</Alert>
                            )}
                    </>
                )}
            </FormContextProvider>
        );
    }

    public showAnamnesisFields() {
        const anamnesisFields =
            this.props.request.customControlData.somnologyOrganizationType !==
            SomnologyOrganizationType.ksyosMedicalCenter
                ? anamnesisFieldsNsi
                : anamnesisFieldsKsyosSleep;

        return anamnesisFields.map((field) => {
            const valueKey = `${field.key}_value`;
            const remarkKey = `${field.key}_remark`;

            return (
                <StackedFormGroup
                    hasErrors={
                        this.props.errors.has(`customControlData.${valueKey}`) ||
                        this.props.errors.has(`customControlData.${remarkKey}`)
                    }
                    key={valueKey + remarkKey}
                >
                    <RadioGroupField
                        automation={field.key}
                        error={this.props.errors.get(`customControlData.${valueKey}`)}
                        fieldKey={valueKey}
                        isRequired
                        label={field.label}
                        name={`${field.label}`}
                        options={['Ja', 'Nee', 'Onbekend']}
                    />
                    <TextAreaField
                        automation={`remark-${field.key}`}
                        error={this.props.errors.get(`customControlData.${remarkKey}`)}
                        fieldKey={remarkKey}
                        label=""
                        placeholder={`${translateKey('common.remarks')}...`}
                        value={this.props.customControlData[`${remarkKey}`]}
                    />
                </StackedFormGroup>
            );
        });
    }

    private async handleChangeComorbiditeiten<K extends keyof IComorbiditeitenKsyosMedicalCenter>(
        name: K,
        value: IComorbiditeitenKsyosMedicalCenter[K]
    ) {
        if (this.props.customControlData.comorbiditeiten === null) {
            throw new Error();
        }

        const customControlDataUpdate: Partial<IOsasc1CustomControlData> = {
            comorbiditeiten: {
                ...this.props.customControlData.comorbiditeiten,
                [name]: value,
            },
        };

        if (value) {
            customControlDataUpdate.comorbiditeitenChoice = ComorbiditiesChoice.ja;
        }

        await this.props.handleChangeCustomControl(customControlDataUpdate);
    }

    private async validateOneCustomControl(name: keyof IOsasc1CustomControlData) {
        await this.props.validateOneCustomControl(String(name));
        await this.props.validateOneCustomControl(String('consult.' + name));
    }

    private showPolicy() {
        let allFieldsAreFilled = true;
        for (const field of anamnesisFieldsNsi) {
            const valueKey = `${field.key}_value`;
            allFieldsAreFilled = allFieldsAreFilled && this.props.customControlData[valueKey] !== null;
            if (!allFieldsAreFilled) {
                break;
            }
        }

        return allFieldsAreFilled;
    }

    private examinationRequired() {
        let positiveAnswer = false;
        for (const field of anamnesisFieldsNsi) {
            const valueKey = `${field.key}_value`;
            positiveAnswer = this.props.customControlData[valueKey] === 'Ja';
            if (positiveAnswer) {
                break;
            }
        }

        return positiveAnswer;
    }
}

export function osasc1V1DiagnosticReportInitializeCustomControlData() {
    return {
        overmatige_slaperigheid_overdag_value: null,
        stokkende_ademhaling_value: null,
        herhaald_wakker_schrikken_value: null,
        niet_verfrissende_slaap_value: null,
        vermoeidheid_overdag_value: null,
        concentratieverlies_value: null,
        snurken_value: null,
        ademstops_value: null,
        humeur_kort_lontje_depressieve_gevoelens_value: null,
        geheugenproblemen_value: null,
        ervaring_te_stikken_tijdens_slaap_value: null,
        overmatige_slaperigheid_overdag_remark: null,
        stokkende_ademhaling_remark: null,
        herhaald_wakker_schrikken_remark: null,
        niet_verfrissende_slaap_remark: null,
        vermoeidheid_overdag_remark: null,
        concentratieverlies_remark: null,
        snurken_remark: null,
        ademstops_remark: null,
        humeur_kort_lontje_depressieve_gevoelens_remark: null,
        geheugenproblemen_remark: null,
        ervaring_te_stikken_tijdens_slaap_remark: null,
        anamnese_overig: null,
        comorbiditeitenChoice: null,
        comorbiditeiten: {
            burn_out_of_depressieve_klachten: false,
            cardiale_problematiek: false,
            diabetes_mellitus: false,
            hoge_bloeddruk: false,
            anders_text: null,
            anders_checked: false,
        },
        dentitie_goed_gesaneerd_value: null,
        dentitie_goed_gesaneerd_remark: null,
        gebitsprothese_value: null,
        gewicht: null,
        lengte: null,
        consultationPlace: null,
        tonsilSize: null,
        tongueSize: null,
        retrognathism: null,
        poligraphyExam: null,
        somnologyOrganizationType: null,
        bmiMeasuredBy: null,
        bmiMeasuredDate: null,
    };
}
